import { getPlatformLogo } from '../../assets/platforms/logos';
import { isShopify, type Platform } from '../../context/env-context';
import type { I18nPrefix } from '../../i18n/prefix';
import type { PlatformFormValues } from './types';

export const PLATFORM_PATH = '/integrations/platform';
export const METAFIELDS_PATH = `${PLATFORM_PATH}/metafields`;

const PAGE = 'features:platform.pages.platform_page' satisfies I18nPrefix;

export type DisplayPlatform = Exclude<Platform, 'shopify_plus'>;
type StoreIdPlatform = Exclude<DisplayPlatform, 'custom'>;

type PlatformNameKey = `${typeof PAGE}.form.platforms.${DisplayPlatform}.name`;
type PlatformSpecificsKey = `${typeof PAGE}.form.platforms.${DisplayPlatform}.specifics`;
type StoreIdLabelKey = `features:platform.components.store_id.label_${StoreIdPlatform}`;

type StoreIdExampleKey =
  | 'platform.components.store_id.helper_example_shopify'
  | 'platform.components.store_id.helper_example_bigcommerce'
  | 'platform.components.store_id.helper_example_wix';

type StoreIdHelperKey = 'features:platform.components.store_id.magento_helper';

type CredentialSuffix =
  | 'woocommerce_api_key'
  | 'woocommerce_api_secret'
  | 'basic_auth_username'
  | 'basic_auth_password'
  | 'consumer_key'
  | 'consumer_secret'
  | 'access_token'
  | 'access_token_secret';
type CredentialLabelKey = `features:platform.components.credential.${CredentialSuffix}.label`;
type CredentialPlaceholderKey =
  `features:platform.components.credential.${CredentialSuffix}.placeholder`;

function displayPlatform(platform: Platform): DisplayPlatform {
  return platform === 'shopify_plus' ? 'shopify' : platform;
}

export const PLATFORM_I18N = {
  page: PAGE,
  platformName: (platform: Platform): PlatformNameKey =>
    `${PAGE}.form.platforms.${displayPlatform(platform)}.name`,
  platformSpecifics: (platform: Platform): PlatformSpecificsKey =>
    `${PAGE}.form.platforms.${displayPlatform(platform)}.specifics`,
  storeIdLabel: (platform: StoreIdPlatform): StoreIdLabelKey =>
    `features:platform.components.store_id.label_${platform}`,
  credentialLabel: (suffix: CredentialSuffix): CredentialLabelKey =>
    `features:platform.components.credential.${suffix}.label`,
  credentialPlaceholder: (suffix: CredentialSuffix): CredentialPlaceholderKey =>
    `features:platform.components.credential.${suffix}.placeholder`,
} as const;

export type PlatformOption = {
  key: DisplayPlatform;
  nameKey: PlatformNameKey;
  specificsKey: PlatformSpecificsKey;
  logo: string;
};

export const PLATFORM_OPTIONS: PlatformOption[] = [
  {
    key: 'shopify',
    nameKey: PLATFORM_I18N.platformName('shopify'),
    specificsKey: PLATFORM_I18N.platformSpecifics('shopify'),
    logo: getPlatformLogo('shopify'),
  },
  {
    key: 'bigcommerce',
    nameKey: PLATFORM_I18N.platformName('bigcommerce'),
    specificsKey: PLATFORM_I18N.platformSpecifics('bigcommerce'),
    logo: getPlatformLogo('bigcommerce'),
  },
  {
    key: 'wix',
    nameKey: PLATFORM_I18N.platformName('wix'),
    specificsKey: PLATFORM_I18N.platformSpecifics('wix'),
    logo: getPlatformLogo('wix'),
  },
  {
    key: 'woocommerce',
    nameKey: PLATFORM_I18N.platformName('woocommerce'),
    specificsKey: PLATFORM_I18N.platformSpecifics('woocommerce'),
    logo: getPlatformLogo('woocommerce'),
  },
  {
    key: 'magento',
    nameKey: PLATFORM_I18N.platformName('magento'),
    specificsKey: PLATFORM_I18N.platformSpecifics('magento'),
    logo: getPlatformLogo('magento'),
  },
  {
    key: 'custom',
    nameKey: PLATFORM_I18N.platformName('custom'),
    specificsKey: PLATFORM_I18N.platformSpecifics('custom'),
    logo: getPlatformLogo('custom'),
  },
];

export type StoreIdFieldConfig = {
  labelKey: StoreIdLabelKey;
  prefix?: string;
  placeholder: string;
  pattern?: string;
  patternTitle?: string;
  // `messageKey` resolves to a full sentence containing `<url/>` and `<id/>` tags,
  // which `Trans` replaces with the rendered template and highlight.
  example?: { messageKey: StoreIdExampleKey; template: string; highlight: string };
  redirectsForAuth?: boolean;
  helperKey?: StoreIdHelperKey;
  learnMoreUrl?: string;
};

export const STORE_ID_FIELDS: Partial<Record<StoreIdPlatform, StoreIdFieldConfig>> = {
  shopify: {
    labelKey: PLATFORM_I18N.storeIdLabel('shopify'),
    prefix: 'https://admin.shopify.com/store/',
    placeholder: 'your-store-id',
    example: {
      messageKey: 'platform.components.store_id.helper_example_shopify',
      template: 'https://admin.shopify.com/store/your-store-id',
      highlight: 'your-store-id',
    },
    learnMoreUrl: 'https://docs.sparklayer.io/installing-shopify-sparklayer#B3Uot',
    redirectsForAuth: true,
  },
  bigcommerce: {
    labelKey: PLATFORM_I18N.storeIdLabel('bigcommerce'),
    placeholder: 'abcd1234',
    pattern: '^[a-zA-Z0-9]+$',
    patternTitle: 'Only letters and numbers are allowed',
    example: {
      messageKey: 'platform.components.store_id.helper_example_bigcommerce',
      template: 'https://store-abcd1234.mybigcommerce.com/',
      highlight: 'abcd1234',
    },
    learnMoreUrl: 'https://docs.sparklayer.io/installing-bigcommerce-sparklayer#B3Uot',
    redirectsForAuth: true,
  },
  wix: {
    labelKey: PLATFORM_I18N.storeIdLabel('wix'),
    prefix: 'https://manage.wix.com/dashboard/',
    placeholder: 'abc12345-0000-0000-0000-000000000000',
    pattern:
      '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(?<![ /])$',
    patternTitle: 'abc12345-0000-0000-0000-000000000000',
    example: {
      messageKey: 'platform.components.store_id.helper_example_wix',
      template: 'https://manage.wix.com/dashboard/abc12345-0000-0000-0000-000000000000',
      highlight: 'abc12345-0000-0000-0000-000000000000',
    },
    learnMoreUrl: 'https://docs.sparklayer.io/installingwix#B3Uot',
    redirectsForAuth: true,
  },
  woocommerce: {
    labelKey: PLATFORM_I18N.storeIdLabel('woocommerce'),
    prefix: 'https://',
    placeholder: 'your-store-id',
  },
  magento: {
    labelKey: PLATFORM_I18N.storeIdLabel('magento'),
    prefix: 'https://',
    placeholder: 'your-store-id',
    helperKey: 'features:platform.components.store_id.magento_helper',
  },
};

export type CredentialFieldConfig = {
  key: keyof PlatformFormValues;
  labelKey: CredentialLabelKey;
  placeholderKey: CredentialPlaceholderKey;
  type?: 'text' | 'password';
};

export const CREDENTIAL_FIELDS: Partial<
  Record<'woocommerce' | 'magento', CredentialFieldConfig[]>
> = {
  woocommerce: [
    {
      key: 'woocommerce_api_key',
      labelKey: PLATFORM_I18N.credentialLabel('woocommerce_api_key'),
      placeholderKey: PLATFORM_I18N.credentialPlaceholder('woocommerce_api_key'),
    },
    {
      key: 'woocommerce_api_secret',
      labelKey: PLATFORM_I18N.credentialLabel('woocommerce_api_secret'),
      placeholderKey: PLATFORM_I18N.credentialPlaceholder('woocommerce_api_secret'),
    },
    {
      key: 'woocommerce_basic_user',
      labelKey: PLATFORM_I18N.credentialLabel('basic_auth_username'),
      placeholderKey: PLATFORM_I18N.credentialPlaceholder('basic_auth_username'),
    },
    {
      key: 'woocommerce_basic_pass',
      labelKey: PLATFORM_I18N.credentialLabel('basic_auth_password'),
      placeholderKey: PLATFORM_I18N.credentialPlaceholder('basic_auth_password'),
      type: 'password',
    },
  ],
  magento: [
    {
      key: 'magento_consumer_key',
      labelKey: PLATFORM_I18N.credentialLabel('consumer_key'),
      placeholderKey: PLATFORM_I18N.credentialPlaceholder('consumer_key'),
    },
    {
      key: 'magento_consumer_secret',
      labelKey: PLATFORM_I18N.credentialLabel('consumer_secret'),
      placeholderKey: PLATFORM_I18N.credentialPlaceholder('consumer_secret'),
    },
    {
      key: 'magento_access_token',
      labelKey: PLATFORM_I18N.credentialLabel('access_token'),
      placeholderKey: PLATFORM_I18N.credentialPlaceholder('access_token'),
    },
    {
      key: 'magento_access_token_secret',
      labelKey: PLATFORM_I18N.credentialLabel('access_token_secret'),
      placeholderKey: PLATFORM_I18N.credentialPlaceholder('access_token_secret'),
    },
  ],
};

export const PLATFORM_DOCS_URLS = {
  learnMore: 'https://docs.sparklayer.io/dashboard-configurations#JJHwx',
  autoComplete: 'https://docs.sparklayer.io/automation-and-workflows#-Rca8',
  disableInvite: 'https://docs.sparklayer.io/sales-agent-ordering#BGOTM',
  metafields: 'https://docs.sparklayer.io/metafields#sleR1',
  metafieldsPage: 'https://docs.sparklayer.io/dashboard-configurations',
} as const;

export function supportsMetafields(platform?: Platform): boolean {
  return isShopify(platform) || platform === 'woocommerce' || platform === 'magento';
}
